import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { entities, uploadFile, getDisplayMessage, openUploadInNewTab } from "@/api/apiClient";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Save,
  Loader2,
  ClipboardList,
  Upload,
  Download,
} from "lucide-react";
import { toast } from "sonner";

function safeDataForInput(value) {
  if (value == null) return "";
  const s = String(value);
  return s.includes("T") ? s.split("T")[0] : s;
}

export default function CriarOrdemServicoPage() {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const contratoId = urlParams.get("contrato_id");
  const id = urlParams.get("id"); // edição
  const isViewOnly = urlParams.get("view") === "1";

  const [formData, setFormData] = useState({
    numero_os: "",
    data_abertura: "",
    descricao: "",
    orgao_secretaria: "",
    setor_responsavel: "",
    prioridade: "Média",
    prazo_execucao_dias: "",
    data_inicio_execucao: "",
    data_conclusao: "",
    status: "Aberta",
    valor_os: "",
    anexo_url: "",
    anexo_nome: "",
  });
  const [uploadingAnexo, setUploadingAnexo] = useState(false);

  const { data: ordensList = [] } = useQuery({
    queryKey: ["ordens-servico", contratoId],
    queryFn: () => entities.OrdemServico.filter({ contrato_id: contratoId }),
    enabled: !!contratoId && !!id,
  });

  const osEdit = id ? ordensList.find((o) => o.id === id) : null;

  useEffect(() => {
    if (osEdit) {
      setFormData({
        numero_os: osEdit.numero_os || "",
        data_abertura: safeDataForInput(osEdit.data_abertura) || "",
        descricao: osEdit.descricao || "",
        orgao_secretaria: osEdit.orgao_secretaria || "",
        setor_responsavel: osEdit.setor_responsavel || "",
        prioridade: osEdit.prioridade || "Média",
        prazo_execucao_dias: osEdit.prazo_execucao_dias ?? "",
        data_inicio_execucao: safeDataForInput(osEdit.data_inicio_execucao) || "",
        data_conclusao: safeDataForInput(osEdit.data_conclusao) || "",
        status: osEdit.status || "Aberta",
        valor_os: osEdit.valor_os != null ? String(osEdit.valor_os) : "",
        anexo_url: osEdit.anexo_url || "",
        anexo_nome: osEdit.anexo_nome || "",
      });
    }
  }, [osEdit]);

  const criarMutation = useMutation({
    mutationFn: (dados) => entities.OrdemServico.create(dados),
    onSuccess: () => {
      toast.success("Ordem de serviço criada com sucesso!");
      navigate(createPageUrl(`VisualizarContrato?id=${contratoId}&aba=ordem-servico`));
    },
    onError: (error) => {
      toast.error(getDisplayMessage(error, "Erro ao criar ordem de serviço. Tente novamente."));
    },
  });

  const atualizarMutation = useMutation({
    mutationFn: ({ id: osId, dados }) => entities.OrdemServico.update(osId, dados),
    onSuccess: () => {
      toast.success("Ordem de serviço atualizada com sucesso!");
      navigate(createPageUrl(`VisualizarContrato?id=${contratoId}&aba=ordem-servico`));
    },
    onError: (error) => {
      toast.error(getDisplayMessage(error, "Erro ao atualizar ordem de serviço. Tente novamente."));
    },
  });

  const handleChange = (campo, valor) => {
    setFormData((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleAnexo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Arquivo muito grande! Máximo 50MB.");
      return;
    }
    setUploadingAnexo(true);
    try {
      const { file_url } = await uploadFile(file);
      handleChange("anexo_url", file_url);
      handleChange("anexo_nome", file.name);
      toast.success("Anexo enviado!");
    } catch (error) {
      toast.error(getDisplayMessage(error, "Erro ao enviar anexo. Tente novamente."));
    } finally {
      setUploadingAnexo(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isViewOnly) return;
    if (!contratoId) {
      toast.error("Contrato não informado.");
      return;
    }
    const rawPrazo = formData.prazo_execucao_dias ? parseInt(formData.prazo_execucao_dias, 10) : null;
    const prazo_execucao_dias = rawPrazo != null && Number.isFinite(rawPrazo) ? rawPrazo : null;
    const valorOsParsed = formData.valor_os ? parseFloat(String(formData.valor_os).replace(",", ".")) : NaN;
    const valor_os = Number.isFinite(valorOsParsed) ? valorOsParsed : null;
    const base = {
      numero_os: formData.numero_os || null,
      data_abertura: formData.data_abertura || null,
      descricao: formData.descricao || null,
      orgao_secretaria: formData.orgao_secretaria || null,
      setor_responsavel: formData.setor_responsavel || null,
      prioridade: formData.prioridade || null,
      prazo_execucao_dias,
      data_inicio_execucao: formData.data_inicio_execucao || null,
      data_conclusao: formData.data_conclusao || null,
      status: formData.status || null,
      valor_os,
      anexo_url: formData.anexo_url || null,
      anexo_nome: formData.anexo_nome || null,
    };
    if (id) {
      atualizarMutation.mutate({ id, dados: base });
    } else {
      criarMutation.mutate({ ...base, contrato_id: contratoId });
    }
  };

  const isPending = criarMutation.isPending || atualizarMutation.isPending;

  if (contratoId && id && ordensList.length > 0 && !osEdit) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate(createPageUrl(`VisualizarContrato?id=${contratoId}`))}
            className="mb-4 -ml-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-blue-600" />
            {isViewOnly ? "Visualizar Ordem de Serviço" : id ? "Editar Ordem de Serviço" : "Nova Ordem de Serviço"}
          </h1>
          <p className="text-slate-600 mt-1">
            {id ? "Altere os dados da ordem de serviço" : "Registre uma nova ordem de serviço do contrato"}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Dados da Ordem de Serviço</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Número da OS</Label>
                  <Input
                    value={formData.numero_os}
                    onChange={(e) => handleChange("numero_os", e.target.value)}
                    placeholder="Ex: 001/2026"
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                </div>
                <div>
                  <Label>Data da Abertura</Label>
                  <Input
                    type="date"
                    value={formData.data_abertura}
                    onChange={(e) => handleChange("data_abertura", e.target.value)}
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                </div>
              </div>

              <div>
                <Label>Descrição / Escopo</Label>
                <Textarea
                  value={formData.descricao}
                  onChange={(e) => handleChange("descricao", e.target.value)}
                  placeholder="Descreva o que contempla a ordem de serviço"
                  rows={3}
                  readOnly={isViewOnly}
                  disabled={isViewOnly}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Órgão/Secretaria</Label>
                  <Input
                    value={formData.orgao_secretaria}
                    onChange={(e) => handleChange("orgao_secretaria", e.target.value)}
                    placeholder="Ex: Secretaria de Fazenda"
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                </div>
                <div>
                  <Label>Setor Responsável</Label>
                  <Input
                    value={formData.setor_responsavel}
                    onChange={(e) => handleChange("setor_responsavel", e.target.value)}
                    placeholder="Ex: TI"
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                </div>
              </div>

              <div>
                <Label>Prioridade</Label>
                <Select value={formData.prioridade} onValueChange={(v) => handleChange("prioridade", v)} disabled={isViewOnly}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Baixa">Baixa</SelectItem>
                    <SelectItem value="Média">Média</SelectItem>
                    <SelectItem value="Alta">Alta</SelectItem>
                    <SelectItem value="Urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Prazo de execução (dias)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.prazo_execucao_dias}
                    onChange={(e) => handleChange("prazo_execucao_dias", e.target.value)}
                    placeholder="Ex: 30"
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                </div>
                <div>
                  <Label>Valor da OS (R$)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.valor_os}
                    onChange={(e) => handleChange("valor_os", e.target.value)}
                    placeholder="Ex: 15000,00"
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Data de início da execução</Label>
                  <Input
                    type="date"
                    value={formData.data_inicio_execucao}
                    onChange={(e) => handleChange("data_inicio_execucao", e.target.value)}
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                </div>
                <div>
                  <Label>Data da conclusão</Label>
                  <Input
                    type="date"
                    value={formData.data_conclusao}
                    onChange={(e) => handleChange("data_conclusao", e.target.value)}
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                </div>
              </div>

              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => handleChange("status", v)} disabled={isViewOnly}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aberta">Aberta</SelectItem>
                    <SelectItem value="Em andamento">Em andamento</SelectItem>
                    <SelectItem value="Concluída">Concluída</SelectItem>
                    <SelectItem value="Cancelada">Cancelada</SelectItem>
                    <SelectItem value="Suspensa">Suspensa</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Anexo da Ordem de Serviço</Label>
                {!isViewOnly && (
                  <>
                    <Input
                      type="file"
                      className="hidden"
                      id="os-anexo"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                      onChange={handleAnexo}
                      disabled={uploadingAnexo}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full mt-2"
                      disabled={uploadingAnexo}
                      onClick={() => document.getElementById("os-anexo")?.click()}
                    >
                      {uploadingAnexo ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> {formData.anexo_nome || "Selecionar arquivo"}</>
                      )}
                    </Button>
                  </>
                )}
                {formData.anexo_url && formData.anexo_nome && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                    <Download className="w-4 h-4" />
                    <button type="button" onClick={() => openUploadInNewTab(formData.anexo_url)} className="text-blue-600 hover:underline text-left">{formData.anexo_nome}</button>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(createPageUrl(`VisualizarContrato?id=${contratoId}`))}
                >
                  {isViewOnly ? "Voltar" : "Cancelar"}
                </Button>
                {!isViewOnly && (
                  <Button type="submit" disabled={isPending} className="bg-blue-600 hover:bg-blue-700">
                    {isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> {id ? "Salvar alterações" : "Criar Ordem de Serviço"}</>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}
